import React, { useRef, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDownload, faUpload } from '@fortawesome/free-solid-svg-icons';
import YearFilter from 'components/shared/YearFilter';
import { CORREDOR_CP_CHANGE_FILTER_YEAR } from '../../../redux-store/constants/action-types';
import FileUploader from '../../shared/FileUploader';

import { API, BASE_URL, FRONT_MF, SEARCH_DELAY_MS } from '../../../CONST';
import {
  cellDateOperation,
  cellNumOperationCenter,
  cellOperation,
  headerDoubleRowOperation,
  headerOperation,
} from '../../../helpers/tables';
import { fileDownload } from '../../../helpers/helpers';
import BaseTable from '../../shared/BaseTable';
import DownloadBtn from '../../shared/DownloadBtn';
import { getLiquidaciones } from '../../../redux-store/actions/corredorCredito';
import CorredorLiquidacionModal from './CorredorLiquidacionModal';

const CorredorLiquidacionList = (props) => {
  const {
    dispatch,
    user,
    loading,
    liquidacion,
    selectedYear,
    xlsxUrl,
    pdfUrl,
    corredor,
    loginIdToken,
    tipo,
  } = props;

  const tableRef = useRef(null);
  const filteringRef = useRef(false);
  const filterParamsRef = useRef('');
  const [selectedLiquidacion, setSelectedLiquidacion] = useState(null);

  const fetchDataWithDebounce = useRef(
    _.debounce((tableState) => {
      fetchData();
    }, SEARCH_DELAY_MS)
  ).current;

  const fetchData = () => {
    filteringRef.current = false;

    const yearFilter = `periodo__year=${selectedYear.year()}`;
    let filterParams = `${yearFilter}&tipo__nemotecnico=${tipo}&`;

    if (user.is_orsan && corredor) {
      filterParams = `${filterParams}corredor_id=${corredor.id}`;
    }

    // only fetch data if filters have changed
    if (filterParams !== filterParamsRef.current) {
      filterParamsRef.current = filterParams;
      dispatch(getLiquidaciones(filterParams));
    }
  };

  const fetchStrategy = (tableState) => {
    if (filteringRef.current) {
      return fetchDataWithDebounce(tableState);
    }
    return fetchData();
  };

  const onFilteredChange = () => {
    filteringRef.current = true;
  };


  useEffect(() => {
    if (tableRef.current) {
      fetchStrategy(tableRef.current.state);
    }
  }, [selectedYear, corredor]);

  const handleUploadResponse = (response) => {    
    if (response && response.file) {
      filterParamsRef.current = '';
      fetchData();
    }
  };

  const columns = [
    {
      Header: headerOperation('Período'),
      id: 'periodo',
      accessor: (i) => cellDateOperation(i.periodo, FRONT_MF),
      minWidth: 100,
      sortable: false,
      filterable: false,
    },
    {
      Header: headerDoubleRowOperation('Prima Neta', 'Recaudada ($)'),
      id: 'prima_neta',
      accessor: (i) => cellNumOperationCenter(i.prima_neta),
      minWidth: 100,
      sortable: false,
      filterable: false,
    },
    {
      Header: headerDoubleRowOperation('Comisión Neta', 'Generada ($)'),
      id: 'comision_neta',
      accessor: (i) => cellNumOperationCenter(i.comision_neta),
      sortable: false,
      minWidth: 100,
      filterable: false,
    },
    {
      Header: headerDoubleRowOperation('Folio de', 'Liquidación'),
      id: 'folio_liquidacion',
      accessor: (i) => (
        <div style={{ textAlign: 'center' }}>
          {i.folio_liquidacion ? (
            <span
              onClick={() => setSelectedLiquidacion(i)}
              style={{
                color: '#0055B8',
                cursor: 'pointer',
                textDecoration: 'underline',
                fontWeight: 500,
              }}
              title="Ver detalle de liquidación"
            >
              {i.folio_liquidacion}
            </span>
          ) : (
            <span style={{ color: '#999' }}>—</span>
          )}
        </div>
      ),
      sortable: false,
      filterable: false,
      minWidth: 100,
    },
    {
      Header: headerOperation('Factura'),
      id: 'actions',
      accessor: (i) => {
        const facturaDownloadUrl = `${BASE_URL}${API.liquidacion}/${i.id}/download/`;
        const finalFacturaDownloadUrl = corredor
          ? `${facturaDownloadUrl}?corredor_id=${corredor.id}`
          : facturaDownloadUrl;
 
        const uploadUrl = `${BASE_URL}${API.liquidacion}/${i.id}/`;
        const finalUploadUrl = corredor
          ? `${uploadUrl}?corredor_id=${corredor.id}`
          : uploadUrl;
 
        return (
          <div className="table-cell" style={{ textAlign: 'center' }}>
            <span title={i.folio_liquidacion ? "Subir factura" : "Sin folio de liquidación"} style={{ marginLeft: 10 }}>
              {i.folio_liquidacion ? (
                <FileUploader
                  type="link"
                  uploadUrl={finalUploadUrl}
                  inputId={`factura-uploader-${i.id}`}
                  accept=".xlsx,.pdf,.xls"
                  handleResponse={handleUploadResponse}
                  stateData={{ id: 1 }}
                  maxFileSize={10485760}
                  confirmButtonText="Confirmar subida"
                  showMontosTable={false}
                />
              ) : (
                <span style={{ fontSize: '18px', color: '#CCCCCC', cursor: 'not-allowed' }}>
                  <FontAwesomeIcon icon={faUpload} />
                </span>
              )}
            </span>
            
            <span title={i.factura_id ? "Descargar factura" : "Sin factura"} style={{ marginLeft: 10 }}>
              <span
                onClick={() => i.factura_id && fileDownload(finalFacturaDownloadUrl, loginIdToken)}
                style={{
                  fontSize: '18px',
                  color: i.factura_id ? '#0055B8' : '#CCCCCC',
                  cursor: i.factura_id ? 'pointer' : 'not-allowed',
                }}
              >
                <FontAwesomeIcon icon={faDownload} />
              </span>
            </span>
          </div>
        );
      },
      filterable: false,
      sortable: false,
      minWidth: 100,
    },
  ];

  return (
    <>
      <div style={{ display: 'grid', gridTemplateColumns: '500px 700px', alignItems: 'start' }}>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '65px auto',
            gridGap: '4px 15px',
            padding: '0 0 16px 16px',
            alignItems: 'center',
          }}
        >
          {!user.is_orsan && (
            <>
              <div className="orsan-label">Nombre:</div>
              <div className="orsan-value">{user.organization_name}</div>
              <div className="orsan-label">RUT:</div>
              <div className="orsan-value">{user.organization_rut}</div>
            </>
          )}
          <div className="orsan-label">Año:</div>
          <YearFilter
            dispatch={dispatch}
            year={selectedYear}
            changeDateActionName={CORREDOR_CP_CHANGE_FILTER_YEAR}
          />
        </div>
      </div>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr auto auto',
          gridGap: '10px',
          marginTop: '15px',
          marginBottom: '6px',
        }}
      >
        {/*
        <div style={{ gridColumnStart: 2 }}>
          <DownloadBtn
            id="xlsx-download"
            text="Template"
            tipText="Descargar Template Excel"
            url={`${BASE_URL}${API.liquidacion}/download_xlsx_template/`}
          />
        </div>
        
        <div style={{ gridColumnStart: 2 }}>
          <DownloadBtn
            id="xlsx-download"
            text="XLSX"
            tipText="Descargar Planilla Excel"
            url={xlsxUrl}
          />
        </div>
        
        <div style={{ gridColumnStart: 3 }}>
          <DownloadBtn id="pdf-download" text="PDF" tipText="Descargar PDF" url={pdfUrl} />
        </div>
         */}
      </div>

      <BaseTable
        tableRef={tableRef}
        columns={columns}
        data={liquidacion.list}
        filterable
        loading={loading}
        onFetchData={fetchStrategy}
        onFilteredChange={onFilteredChange}
        style={{ maxHeight: '63vh' }}
      />
      {selectedLiquidacion && (
      <CorredorLiquidacionModal
        liquidacion={selectedLiquidacion}
        onClose={() => setSelectedLiquidacion(null)}
        corredor={corredor}
      />
      )}
    </>
  );
};

CorredorLiquidacionList.propTypes = {
  dispatch: PropTypes.func.isRequired,
  user: PropTypes.object.isRequired,
  loading: PropTypes.bool.isRequired,
  liquidacion: PropTypes.object.isRequired,
  selectedYear: PropTypes.object.isRequired,
  xlsxUrl: PropTypes.string.isRequired,
  pdfUrl: PropTypes.string.isRequired,
  corredor: PropTypes.object,
  loginIdToken: PropTypes.string.isRequired,
  tipo: PropTypes.string.isRequired,
};
const mapStateToProps = (state) => ({
  xlsxUrl: state.corredorCredito.liquidacion.xlsxUrl,
  pdfUrl: state.corredorCredito.liquidacion.pdfUrl,
  selectedYear: state.corredorCredito.yearFilter,
  loading: state.corredorCredito.loading,
  user: state.login.user,
  loginIdToken: state.login.id_token,
  liquidacion: state.corredorCredito.liquidacion,
});

export default connect(mapStateToProps)(CorredorLiquidacionList);


